import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Accueil', href: '#accueil' },
  { label: 'Hebergements', href: '#hebergements' },
  { label: 'Services', href: '#services' },
  { label: 'Cuisine', href: '/cuisine-professionnelle' },
  { label: 'Traiteurs', href: '/traiteurs-partenaires' },
  { label: 'DJ', href: '/dj-partenaires' },
  { label: 'Galerie', href: '#galerie' },
  { label: 'Avis', href: '/avis' },
  { label: 'Contact', href: '#contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomepage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith('/')) { navigate(href); return; }
    if (isHomepage) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + href);
    }
  };

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed left-0 right-0 z-50 transition-all duration-500 top-[36px] ${
          scrolled || !isHomepage ? 'nav-shrunk' : 'nav-full'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); isHomepage ? handleNavClick('#accueil') : navigate('/'); }}
            data-testid="nav-logo"
            className="flex items-center gap-2"
          >
            <span
              className={`font-['Cormorant_Garamond'] font-normal tracking-[-0.02em] transition-all duration-500 ${
                scrolled || !isHomepage ? 'text-xl text-[#1A1C19]' : 'text-2xl text-white'
              }`}
            >
              Le Domaine du Pilat
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link, i) => (
              <a
                key={`${link.label}-${i}`}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-[12px] font-medium tracking-[0.05em] uppercase transition-colors duration-300 ${
                  scrolled || !isHomepage ? 'text-[#1A1C19]/70 hover:text-[#1A1C19]' : 'text-white/75 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
            <Button
              data-testid="nav-reserve-btn"
              onClick={() => handleNavClick('#reservation')}
              className="rounded-full px-7 py-2.5 bg-[#2E3C30] text-white hover:bg-[#222D23] transition-all active:scale-95 text-[11px] font-semibold tracking-[0.2em] uppercase shadow-[0_4px_20px_rgba(46,60,48,0.25)]"
            >
              Reserver
            </Button>
          </div>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 relative z-[60]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? (
              <X className={`w-6 h-6 ${scrolled || !isHomepage ? 'text-[#1A1C19]' : 'text-white'}`} strokeWidth={1.5} />
            ) : (
              <Menu className={`w-6 h-6 ${scrolled || !isHomepage ? 'text-[#1A1C19]' : 'text-white'}`} strokeWidth={1.5} />
            )}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 animate-in slide-in-from-top-2 duration-200"
          >
            <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <a
                  key={`m-${link.label}-${i}`}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                  className="text-[#1A1C19] text-base font-medium py-3 border-b border-black/5 tracking-wide"
                >
                  {link.label}
                </a>
              ))}
              <Button
                data-testid="mobile-reserve-btn"
                onClick={() => handleNavClick('#reservation')}
                className="rounded-full mt-4 bg-[#2E3C30] text-white hover:bg-[#222D23] text-sm font-semibold tracking-widest uppercase py-3"
              >
                Reserver
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Sticky reserve button (mobile) */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] lg:hidden" style={{ pointerEvents: 'auto' }}>
        <Button
          data-testid="sticky-reserve-btn"
          onClick={() => {
            if (isHomepage) {
              const el = document.querySelector('#reservation');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            } else { navigate('/#reservation'); }
          }}
          className="rounded-full px-8 py-3 bg-[#2E3C30] text-white hover:bg-[#222D23] shadow-[0_12px_40px_-8px_rgba(46,60,48,0.5)] transition-transform active:scale-95 text-sm font-semibold tracking-widest uppercase"
        >
          Reserver
        </Button>
      </div>
    </>
  );
}
